<script setup>
import { useDialogPluginComponent } from 'quasar'
import { reactive, ref, toRefs, onMounted, provide, watch } from 'vue'
import { useQuasar } from 'quasar'
import apiDevice from '../../../api/device.api'
import useDevice from '../../../store/device.store'
import MapComponent from '../../shared/Map.vue'
import { callApi } from '../../../utils'
import { useI18n } from 'vue-i18n'

const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()
const { getDeviceList } = useDevice()
const $q = useQuasar()
const { t } = useI18n()

const props = defineProps({
  id: String,
  fetch: Function
})

const emit = defineEmits(['fetch'])

const { id } = toRefs(props)
const loading = ref(false)
const confirmDialog = ref(false)
const deleteDialog = ref(false)
const data = reactive({
  deviceId: '',
  deviceNumber: '',
  price: null,
  waitingPrice: null,
  parkingTimeDelayAfterCharge: null
})
const location = ref([106.91753580953763, 47.916671069424204])
provide('coordinates', location)
provide('loading', loading)

function showSubmit () {
  confirmDialog.value = true
}

function showDeleteDialog () {
  deleteDialog.value = true
}

async function deleteDevice() {
  try {
    loading.value = true
    await apiDevice.remove(id.value)
    getDeviceList()
    onDialogOK()
  } catch (error) {
    $q.notify({ message: error?.data?.error, type: 'negative' })
  } finally {
    loading.value = false
  }
}

async function submit () {
  try {
    loading.value = true

    await apiDevice.update(
      id.value,
      { 
        deviceId: data.deviceId,
        deviceNumber: data.deviceNumber,
        price: Number(data.price),
        location: location.value,
        waitingPrice: Number(data.waitingPrice),
        parkingTimeDelayAfterCharge: Number(data.parkingTimeDelayAfterCharge),
      }
    )

    props.fetch()
    loading.value = false
    onDialogOK()
  } catch (error) {
    $q.notify({ message: error?.data?.error, type: 'negative' })
  } finally {
    loading.value = false
  }
}

let currentLocation = [106.91753580953763, 47.916671069424204]
function init (params) {
  data.deviceId = String(params.deviceId)
  data.deviceNumber = params.deviceNumber
  data.price = Number(params.price)
  data.waitingPrice = Number(params.waitingPrice)
  data.parkingTimeDelayAfterCharge = Number(params.parkingTimeDelayAfterCharge)
  const nextLocation = Array.isArray(params.location) && params.location.length >= 2
    ? [Number(params.location[0]), Number(params.location[1])]
    : [106.91753580953763, 47.916671069424204]

  location.value = nextLocation
  currentLocation = [...nextLocation]
}

async function fetch () {
  loading.value = true

  const { data } = await apiDevice.getOne(id.value)
  init(data)

  loading.value = false
}

fetch()

const mapOptions = {
  center: [ 106.91753580953763, 47.916671069424204 ],
  zoom: 14,
  pitch: 0,
  bearing: 0,

  minZoom: 5,
  maxZoom: 20,

  dragRotate: false,
  dragPitch: false,
  dragRotatePitch: false,
}
</script>

<template lang='pug'>
.device-update
  q-dialog(ref='dialogRef' persistent)
    q-card.overflow-hidden(style='width: 1000px; max-width: 80vw;')
      q-form(@submit='showSubmit')
        q-card-section
          .text-h6 {{ t('roles.update_title') }}
        q-separator
        q-card-section.scroll(style='max-height: 70vh; min-height: 40vh;')
          .row.q-ma-xs.q-mt-sm
            div(style="width: 50%")
              q-input.q-ma-xs.q-mt-sm(
                stack-label
                outlined
                v-model='data.deviceId'
                :label="t('roles.code')"
                no-error-icon
                hide-bottom-space
                hide-hint
              )
              q-input.q-ma-xs.q-mt-sm(
                stack-label
                outlined
                v-model='data.deviceNumber'
                :label="t('roles.name')"
                no-error-icon
                hide-bottom-space
                hide-hint
              )
              q-input.q-ma-xs.q-mt-sm(
                stack-label
                outlined
                v-model='data.price'
                :label="t('devices.price')"
                mask="##########"
                no-error-icon
                hide-bottom-space
                hide-hint
              )
              q-input.q-ma-xs.q-mt-sm(
                stack-label
                outlined
                v-model='data.waitingPrice'
                :label="t('devices.waiting_price')"
                mask="##########"
                no-error-icon
                hide-bottom-space
                hide-hint
              )
              q-input.q-ma-xs.q-mt-sm(
                stack-label
                outlined
                v-model='data.parkingTimeDelayAfterCharge'
                :label="t('devices.parking_delay')"
                mask="#####"
                no-error-icon
                hide-bottom-space
                hide-hint
              )
            div(style="width: 50%")
              .row.q-ma-xs.q-mt-sm
                q-input(
                  stack-label
                  outlined
                  :model-value="location[0]"
                  @update:model-value="v => location[0] = Number(v)"
                  :suffix='location[0] === currentLocation[0] ? "" : "*"'
                  step="any"
                  label='Long'
                  type="number"
                  no-error-icon
                  hide-bottom-space
                  hide-hint
                  style='width: 50%'
                )
                  template(v-slot:append)
                    q-btn(
                      v-if="location[0] !== currentLocation[0]"
                      icon='close'
                      size='sm'
                      @click='location[0] = currentLocation[0]'
                      dense
                    )
                q-input(
                  stack-label
                  outlined
                  :model-value="location[1]"
                  @update:model-value="v => location[1] = Number(v)"
                  label='Lat'
                  :suffix='location[1] === currentLocation[1] ? "" : "*"'
                  step="any"
                  no-error-icon
                  type="number"
                  hide-bottom-space
                  hide-hint
                  hide-spin-buttons
                  style='width: 50%'
                )
                  template(v-slot:append)
                    q-btn(
                      v-if="location[1] !== currentLocation[1]"
                      icon='close'
                      size='sm'
                      @click='location[1] = currentLocation[1]'
                      dense
                    )
              
              map-component.q-pa-xs(
                :options="mapOptions"
                width="100%"
                height="80%"
                @update:coordinates="updateCoordinates"
              )
        q-separator
        q-card-actions(align='right')
          q-btn(
            flat
            unelevated
            color='red'
            @click='showDeleteDialog'
            :label="t('roles.delete')")
          q-space
          q-btn(
            flat
            unelevated
            color='grey'
            @click='onDialogCancel'
            :label="t('actions.cancel')")
          q-btn(
            flat
            type='submit'
            unelevated
            color='primary'
            :label="t('actions.save')")
            q-separator
          
      q-inner-loading(:showing='loading')

  q-dialog(v-model='confirmDialog' persistent)
    q-card
      q-card-section
        q-icon(size='lg' name='priority_high' color="yellow")
        span {{ t('roles.confirm_update_text') }}
      q-card-actions(align='right')
        q-btn(flat unelevated color='grey' :label="t('actions.no')" v-close-popup)
        q-btn(flat unelevated color='primary' :label="t('actions.yes')" v-close-popup @click='submit')

  q-dialog(v-model='deleteDialog' persistent)
    q-card
      q-card-section
        q-icon(size='lg' name='warning' color='red')
        span {{ t('devices.confirm_remove', { name: data.deviceNumber || data.deviceId }) }}
      q-card-actions(align='right')
        q-btn(flat unelevated color='grey' :label="t('actions.no')" v-close-popup)
        q-btn(flat unelevated color='red' :label="t('actions.yes')" v-close-popup @click='deleteDevice')
</template>

<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>